import { gql } from "@apollo/client";
import moment from "moment";
import N from "numeral";
import { isNil, path } from "ramda";
import {
  getFormStateField,
  getFormDataField,
  getCurrentJob,
} from "../job-posting/helpers";
import { isManagementCategoryJob } from "../PostJob/view";

export const AddJobMutation = gql`
  mutation addNewJob($input: AddJobInput) {
    createJob(input: $input) {
      id
      offices {
        id
        name
      }
      hiringManager {
        id
        lastName
        firstName
      }
      applicantGroup
      jobCategory
      jobTitle
      status
      employmentType
      yearsExperience
      submissionDeadline
      baseCompensation
      baseCompensationMax
      paySchedule
      additionalIncome
      totalCompensation
      contact {
        id
        name
        title
        email
      }
      education
      additionalPGAEducation
      applyVia
      website
      overview
      experienceRequired
      responsibilities
      benefits
      termOfServiceAgreement
      currentStep
      createdAt
      openings {
        id
        openedAt
        status
      }
    }
  }
`;

const getOptionalEditorField = (name, obj, field) => {
  const editorValue = getFormStateField(obj, field, "");
  return editorValue !== "" ? { [name]: editorValue } : null;
};

const getOptionalCurrencyField = (name, obj, field) => {
  const currencyValue = getFormStateField(obj, field, "");
  return currencyValue === "" || currencyValue === "$"
    ? null
    : { [name]: toMoneyString(currencyValue) };
};

const getOfficeValue = (props) => {
  const data = getFormDataField(props, "facility", []);
  const name = getFormStateField(props, "facility");
  const { id } = data.find(({ text }) => text === name) || {};
  return props.isCopy ? id : name;
};

const toMoneyString = (val) =>
  parseFloat(Number(N(val).value()).toFixed(2).toString(), 10).toFixed(2);

export const yearsExperienceMap = {
  "N/A": "0",
  "5+": "5",
};

export const setManagementJobStep = (props, payload) => {
  // isEditOrCopyJob goes off the original job form state, not the new form state.
  const editOrCopyJobCategory = getCurrentJob(props, "job_category");
  // sets true if the path is /edit/:id and the jobs current form state is a management job.
  const isEditManagementJob =
    !props.isCopy && isManagementCategoryJob(editOrCopyJobCategory);

  if (isEditManagementJob) {
    payload.input.currentStep = "Complete";
  } else {
    if (isManagementCategoryJob(payload.input.jobCategory)) {
      payload.input.currentStep = "Pending";
      payload.input.needsAssistance = props.needsAssistance ? "Yes" : "No";
    }
  }
};

export const setDraftToComplete = (props, payload) => {
  // Consultant-created jobs should always be set to Complete.
  const isDraft = path(["currentJob", "current_step"], props) === "Draft";
  if (isDraft) {
    payload.input.currentStep = "Complete";
  }
};

export const getAddJobPaylod = (props) => {
  const yearsExperience = getFormStateField(props, "years_experience", 0);
  const submissionDeadline = getFormStateField(props, "resume_deadline");
  const education = getFormStateField(props, "education", 0);
  const hideCompensation =
    getFormStateField(props, "hideCompensation") === "Hide Compensation";

  let payload = {
    input: {
      office: getOfficeValue(props),
      applicantGroup: getFormStateField(props, "applicant_group", 0),
      jobCategory: getFormStateField(props, "job_category", 0),
      jobTitle: getFormStateField(props, "job_title"),
      employmentType: getFormStateField(props, "position_type", 0),
      yearsExperience: yearsExperienceMap[yearsExperience]
        ? Number(yearsExperienceMap[yearsExperience])
        : Number(getFormStateField(props, "years_experience", 0)),
      submissionDeadline: submissionDeadline
        ? moment(submissionDeadline).endOf("day")
        : null,
      baseCompensation: toMoneyString(
        getFormStateField(props, "base_compensation_min", 0),
      ),
      contactName: getFormStateField(props, "contact_name"),
      contactTitle: getFormStateField(props, "contact_title"),
      contactEmail: getFormStateField(props, "contact_email"),
      overview: getFormStateField(props, "overview"),
      education: education === "N/A" ? null : education,
      additionalPGAEducation: getFormStateField(
        props,
        "additional_pga_education",
      ),
      ...getOptionalCurrencyField(
        "totalCompensation",
        props,
        "total_anticipated_compensation",
      ),
      ...getOptionalCurrencyField(
        "baseCompensationMax",
        props,
        "base_compensation_max",
      ),
      ...getOptionalEditorField("additionalIncome", props, "additional_income"),
      ...getOptionalEditorField(
        "experienceRequired",
        props,
        "experience_required",
      ),
      ...getOptionalEditorField(
        "responsibilities",
        props,
        "essential_responsibilities",
      ),
      ...getOptionalEditorField("benefits", props, "benefits"),
      termOfServiceAgreement: getFormStateField(props, "terms") === true,
      hideCompensation,
    },
  };

  if (getFormStateField(props, "apply_via")) {
    payload.input.applyVia = getFormStateField(props, "apply_via", 0);
  }

  if (getFormStateField(props, "website")) {
    payload.input.website = getFormStateField(props, "website");
  }

  const payScheduleValue = getFormStateField(props, "pay_schedule");
  if (!isNil(payScheduleValue)) {
    payload.input.paySchedule = payScheduleValue;
  }

  setManagementJobStep(props, payload);

  setDraftToComplete(props, payload);

  if (getFormStateField(props, "search_status_question")) {
    const otherSections = getFormStateField(props, "otherSections", []);
    const otherStates = getFormStateField(props, "otherStates", []);
    if (otherSections.length) {
      payload.input.otherSections = otherSections;
    }
    if (otherStates.length) {
      payload.input.otherStates = otherStates;
    }
  }

  if (getFormStateField(props, "contact_phone")) {
    payload.input.contactPhone = getFormStateField(props, "contact_phone");
  }

  return payload;
};
